import React, { useEffect, useRef } from 'react';
import { useApp } from '../context/appContext';

const MapComponent = () => {
  const mapRef = useRef(null);
  const map = useRef(null);
  const storeMarkersRef = useRef([]); // To store references to store markers

  const { nearestStores, visibleStores, currentPage } = useApp();

  // Function to clear all store markers from the map
  const clearMarkers = () => {
    storeMarkersRef.current.forEach(marker => marker.setMap(null)); // Remove each marker from the map
    storeMarkersRef.current = []; // Clear the marker references
  };

  // Utility function to ensure the Google Maps API is loaded
  const loadGoogleMapsScript = () => {
    return new Promise((resolve) => {
      if (typeof window.google !== 'undefined' && window.google.maps) {
        resolve();
      } else {
        const checkInterval = setInterval(() => {
          if (typeof window.google !== 'undefined' && window.google.maps) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
      }
    });
  };

  useEffect(() => {
    // Ensure Google Maps API has loaded before initializing the map
    loadGoogleMapsScript().then(() => {
      // Initialize the map
      map.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: -25.2744, lng: 133.7751 }, // Initial center (Australia)
        zoom: 5,
      });
    });
  }, []);

  useEffect(() => {
    if (currentPage !== 'listing') return; // Only update the map on the listing page

    if (nearestStores && nearestStores.length > 0 && visibleStores > 0 && map.current) {
      // Filter stores to only include "Optical Superstore"
      const filteredStores = nearestStores.filter(store => store.Brand__c === 'Optical Superstore');

      if (filteredStores.length === 0) return; // If no stores match, exit

      // Create LatLngBounds object to adjust the zoom level and fit all markers
      const bounds = new window.google.maps.LatLngBounds();

      // Create a single InfoWindow that will be reused
      const infoWindow = new window.google.maps.InfoWindow();

      clearMarkers(); // Clear any existing store markers

      // Loop through the filteredStores based on the visibleStores limit
      filteredStores.slice(0, visibleStores).forEach(store => {
        const { latitude, longitude } = store.PIAB__Coordinates__c;
        const address = `${store.PIAB__Street__c}, ${store.PIAB__City__c}, ${store.PIAB__State__c} ${store.PIAB__Postal_Code__c}`;

        // Create a marker for each store
        const marker = new window.google.maps.Marker({
          position: { lat: latitude, lng: longitude },
          map: map.current,
          title: store.Name, // Store Name as title
        });

        // Add click listener to open the info window when marker is clicked
        marker.addListener('click', () => {
          // Set the content of the InfoWindow
          infoWindow.setContent(`<strong>${store.Name}</strong><br>${address}<br>Phone: ${store?.PIAB__Phone__c || "None"}`);
          
          // Open the InfoWindow at the marker's position
          infoWindow.open(map.current, marker);
        });

        // Add the marker to the storeMarkers array
        storeMarkersRef.current.push(marker);

        // Extend the bounds to include the store marker
        bounds.extend(new window.google.maps.LatLng(latitude, longitude));
      });

      // Fit the map to the bounds so that all markers are visible
      map.current.fitBounds(bounds);
    }
  }, [nearestStores, visibleStores, currentPage]);

  return (
    <div className="map-container" ref={mapRef} style={{ width: '100%', height: '800px' }}></div>
  );
};

export default MapComponent;
